import { ButtonProps } from '@mui/material';
import React from 'react';
import { RGBColor } from 'react-color';

import {
  COVER_IMAGE,
  EMAIL_MESSAGE,
  EMAIL_SUBJECT,
  EMAIL_TEMPLATE_ID,
  GLOBAL_APP_BAR_LOGO_IMAGE,
  GLOBAL_HEADER_MEDIA,
  GLOBAL_HERO_SECTION_IMAGE,
  MESSAGE,
  NOTE_TEMPLATE_ID,
  RECEIVER_EMAIL,
} from '../constants/templateKeys';
import { IPromiseCallbacks } from './redux';
import { ImageFitEnum } from './shell';
import { BackgroundTypesEnum, ImageSizeEnum } from './upload';

export enum TemplateTypesEnum {
  Note = 'Custom message',
  Email = 'Email',
  ClaimLink = 'Claim link',
}

export enum EmailTemplateTypesEnum {
  DELAYED_SHIPPING = 'delayed_shipping',
  DIGITAL_GIFT = 'digital_gift',
}

export enum ClaimLinkTemplateStatusEnum {
  Live = 'active',
  Hidden = 'not_active',
  Draft = 'draft',
}

export enum ClaimLinkTemplateSourceEnum {
  Campaign = 'campaign',
  Org = 'org',
  Send = 'send',
}

export interface IAddEmailFormValues {
  [RECEIVER_EMAIL]: string;
  [EMAIL_SUBJECT]: string;
  [EMAIL_MESSAGE]: string;
  [EMAIL_TEMPLATE_ID]: string | null;
}

export interface IAddNoteFormValues {
  [MESSAGE]: string;
  [NOTE_TEMPLATE_ID]: string | null;
}

export interface INoteTemplate {
  body: string;
  department_ids: string[];
  is_default: boolean;
  title: string;
  item_id: string;
  org_id: string;
}

export interface IEmailTemplate {
  body: string;
  department_ids: string[];
  item_id: string;
  subject: string;
  type: EmailTemplateTypesEnum;
  is_default?: boolean;
}

// This types used only for the Templates feature in the common list of all templates
export type TCollectiveNoteTemplate = INoteTemplate & { templateType: TemplateTypesEnum };
export type TCollectiveEmailTemplate = IEmailTemplate & { templateType: TemplateTypesEnum };
export type TCollectiveTemplateItem = TCollectiveNoteTemplate | TCollectiveEmailTemplate;

export interface ITemplatesState {
  emails: {
    templates: IEmailTemplate[] | null;
    isLoading: boolean;
    error: any;
  };
  notes: {
    templates: INoteTemplate[] | null;
    isLoading: boolean;
    error: any;
  };
  claimLinks: {
    templates: IClaimLinkTemplate[] | null;
    isLoading: boolean;
    isSilentLoading: boolean;
    error: any;
    totalCount: number;
    draft: IClaimLinkBuilderValues | null;
    draftSnapshot: string | null;
    validationErrors: IClaimLinkTemplateErrors | null;
    validationErrorMessage: string | null;
    validationModal: TemplateValidationModalEnum;
    lastFetchParams: Omit<IFetchClaimLinkTemplatesPayload, 'resolve' | 'reject' | 'silent'> | null;
    currentTemplate: IClaimLinkTemplate | null;
    isCurrentTemplateLoading: boolean;
    isSaving: boolean;
    pageHistory: {
      [key in ClaimLinkEditorPageEnum]: {
        past: IClaimLinkBuilderPageValues[];
        future: IClaimLinkBuilderPageValues[];
      };
    };
  };
  templateDetails: {
    template: Partial<TCollectiveTemplateItem> | null;
    isLoading: boolean;
    error: any;
  };
}

export interface IAddTemplateRequest extends IPromiseCallbacks {
  template: Partial<TCollectiveTemplateItem>;
}

export interface IEditTemplateRequest extends IPromiseCallbacks {
  item: Partial<TCollectiveTemplateItem>;
}

export interface IClaimLinkTemplatePageOverrides {
  header?: {
    media?: {
      image_url: string;
    };
    app_bar?: {
      logo_image_url?: string;
      // Scale factor for the app bar logo, 0.5-2 (BE: header.app_bar.logo_image_size)
      logo_image_size?: number;
      background_color?: string;
    };
    hero_section?: {
      background_color?: string;
      image_url?: string;
      image_fit?: ImageFitEnum;
    };
  };
  buttons?: {
    action?: {
      color: string;
    };
  };
}

export interface IClaimLinkTemplatePageContent {
  header_text: string;
  body_text: string;
  overrides?: IClaimLinkTemplatePageOverrides;
}

export interface IClaimLinkTemplate {
  version?: string;
  cover_image_url: string;
  name: string;
  description: string;
  status: ClaimLinkTemplateStatusEnum;
  department_ids: string[];
  uid: string;
  org_id: string;
  created_by: string;
  updated_by: string;
  archived_by: string;
  created_at: string;
  updated_at: string;
  archived_at: string;
  is_default: boolean;
  is_active: boolean;
  pages: {
    pick_your_gift: IClaimLinkTemplatePageContent;
    pick_your_option: IClaimLinkTemplatePageContent;
    shipping_page: IClaimLinkTemplatePageContent;
  };
  header: {
    media: {
      image_url: string;
    };
    app_bar: {
      logo_image_url: string;
      logo_image_href: string;
      logo_image_size?: number;
      background_color: string;
    };
    hero_section: {
      background_color: string;
      image_url: string;
      image_position: string;
      // image_fit doubles as the hero height mode: Cover = Auto (content-driven height with a
      // fixed floor), Contain = Adapt (claim page grows the hero to the image aspect).
      image_fit: ImageFitEnum;
    };
  };
  buttons: {
    action: IClaimLinkTemplateButtonStyle;
    stepper: {
      color: string;
    };
  };
  background: {
    color: string;
  };
  typography: {
    title: IClaimLinkTemplateTextStyle;
    body: IClaimLinkTemplateTextStyle;
  };
  footer_color?: string;
  enabled_intro_page?: boolean;
  enabled_recipient_note?: boolean;
}

export interface IClaimLinkTemplateButtonStyle {
  color: string;
  radius: number;
}

export interface IClaimLinkTemplateTextStyle {
  bold: boolean;
  italic: boolean;
  color: string;
}

export interface IFetchClaimLinkTemplatesPayload extends IPromiseCallbacks {
  search_query?: string;
  page_size: number;
  page: number;
  department_ids?: string[];
  status?: ClaimLinkTemplateStatusEnum | '';
  created_by?: string;
  silent?: boolean;
  org_id?: string;
}

export interface IClaimLinkTemplatesPageRequest {
  org_id: string;
  page: number;
  page_size: number;
  search_query?: string;
  department_ids?: string[];
  status?: ClaimLinkTemplateStatusEnum;
  created_by?: string;
}

export interface IClaimLinkTemplatesPage {
  templates: IClaimLinkTemplate[];
  total_count: number;
}

export interface IFetchClaimLinkTemplateByIdPayload extends Partial<IPromiseCallbacks> {
  uid: string;
}

export interface IFetchClaimLinkTemplatesResponse {
  claim_page_experiences: IClaimLinkTemplate[];
  total_count: number;
  page_size: number;
  page_number: number;
}

export interface IUpdateClaimLinkTemplateDetailsRequest extends Partial<IPromiseCallbacks> {
  uid: string;
  status?: ClaimLinkTemplateStatusEnum;
  is_default?: boolean;
  name?: string;
  department_ids?: string[];
}

export interface ISaveClaimLinkTemplateRequest extends Partial<IPromiseCallbacks> {
  uid?: string;
  template: Partial<IClaimLinkTemplate>;
}

export interface ICreateClaimLinkTemplateResponse {
  uid: string;
}

export interface ITemplateModalStep {
  id: number;
  label: string;
  value: string;
}

export enum TemplateModalStepEnum {
  Details = 1,
  Branding = 2,
  HeroDefaults = 3,
}

export enum ClaimLinkEditorPageEnum {
  PickYourGift = 'pick_your_gift',
  ProductOptions = 'pick_your_option',
  ShippingDetails = 'shipping_page',
}

export interface IClaimLinkPageOverrides {
  headerMediaUrl?: string;
  logoImageUrl?: string;
  // Percent (50-200); undefined means "inherit the template default"
  logoImageSize?: number;
  appBarBackgroundColor?: RGBColor;
  heroSectionBackgroundColor?: RGBColor;
  heroSectionImageUrl?: string;
  heroSectionImageFit?: ImageFitEnum;
  heroSectionBackgroundType?: BackgroundTypesEnum;
  buttonColor?: RGBColor;
}

export interface IClaimLinkBuilderPageValues {
  headerText: string;
  bodyText: string;
  overrides: IClaimLinkPageOverrides;
}

export interface IClaimLinkBuilderValues {
  uid?: string;
  templateName: string;
  selectedDepartmentIds: string[];
  status: ClaimLinkTemplateStatusEnum;
  is_default?: boolean;
  coverImageUrl: string;

  globalLogoImageUrl: string; // header.app_bar.logo_image_url
  globalLogoImageSize: number; // header.app_bar.logo_image_size, held as a percent (50-200)
  globalLogoImageHref: string; // header.app_bar.logo_image_href
  globalAppBarBackgroundColor: RGBColor; // header.app_bar.background_color
  globalHeaderMediaUrl: string; // header.media.image_url
  globalHeroSectionBackgroundColor: RGBColor; // header.hero_section.background_color
  globalHeroSectionImageUrl: string; // header.hero_section.image_url
  globalHeroSectionImagePosition: string; // header.hero_section.image_position
  globalHeroSectionImageFit: ImageFitEnum; // header.hero_section.image_fit (Cover = Auto, Contain = Adapt)
  // FE-only active hero background selection (color | image). The API model has no such
  // discriminator: with Color active the image URL is kept in builder state but not persisted.
  globalHeroSectionBackgroundType?: BackgroundTypesEnum;
  globalAccentColor: RGBColor; // buttons.action.color
  globalButtonRadius: number; // buttons.action.radius
  backgroundColor: RGBColor;
  pages: {
    [ClaimLinkEditorPageEnum.PickYourGift]: IClaimLinkBuilderPageValues;
    [ClaimLinkEditorPageEnum.ProductOptions]: IClaimLinkBuilderPageValues;
    [ClaimLinkEditorPageEnum.ShippingDetails]: IClaimLinkBuilderPageValues;
  };
  activePageId: ClaimLinkEditorPageEnum;

  // DEPRECATED - keeping for backward compatibility during migration
  headerText?: string;
  bodyText?: string;
  buttonColor?: RGBColor;
  backgroundType?: BackgroundTypesEnum;
  backgroundImageSize?: ImageSizeEnum;
  backgroundImageUrl?: string | null;
  logoImageUrl?: string | null;
  logoImageSize?: number | null;
  headerMediaUrl?: string | null;
}

export type IClaimLinkBuilderFieldUpdate = Omit<Partial<IClaimLinkBuilderValues>, 'pages'> & {
  pages?: Partial<Record<ClaimLinkEditorPageEnum, Partial<IClaimLinkBuilderPageValues>>>;
};

export interface IExperiencePreviewPageOverrides {
  header?: {
    media?: {
      image_url?: string;
    };
    app_bar?: {
      background_color?: string;
      logo_image_url?: string;
      logo_image_size?: number;
    };
    hero_section?: Partial<IHeaderHero>;
  };
  buttons?: {
    action?: {
      color?: string;
    };
  };
}

interface IHeaderHero {
  background_color: string;
  image_url: string;
  image_position: string;
  image_fit: ImageFitEnum;
}

interface IExperiencePageContent {
  header_text?: string;
  body_text?: string;
  overrides?: IExperiencePreviewPageOverrides;
}

interface IPreviewPages {
  pick_your_gift: IExperiencePageContent;
  pick_your_option: IExperiencePageContent;
  shipping_page: IExperiencePageContent;
}

export interface IExperiencePreviewValues {
  experience: {
    header: {
      media: {
        image_url: string;
      };
      app_bar: {
        background_color: string;
        logo_image_url?: string;
        logo_image_size?: number;
      };
      hero_section: IHeaderHero;
    };
    buttons: {
      action: {
        color: string;
      };
    };
    background: {
      color: string;
    };
    pages: IPreviewPages;
  };
}

export interface IExperienceInitPayload {
  data: IExperiencePreviewValues & Record<string, unknown>;
  page: IframeStepEnum;
}

export enum IframeStepEnum {
  PickYourGift = 'pick-your-gift',
  CustomizeOptions = 'customize-options',
  Address = 'address',
}

export interface IExperiencePreviewPage {
  step: IframeStepEnum;
}

export enum ClaimLinkEditorViewTypeEnum {
  Desktop = 'desktop',
  Mobile = 'mobile',
}

export interface IBuilderActionButtonConfig {
  label: string;
  variant: ButtonProps['variant'];
  dataAction: string;
  onClick?: () => void;
  visibleWhen?: ClaimLinkTemplateStatusEnum;
}

export interface ITemplateCardConfig {
  showStatusChip?: boolean;
  showDefaultBanner?: boolean;
  actions?: (template: IClaimLinkTemplate) => React.ReactNode;
  enableEditDetails?: boolean;
  highlightDefault?: boolean;
  isHighlighted?: (template: IClaimLinkTemplate) => boolean;
  campaignClaimLinkTemplateId?: string;
  onCardClick?: (template: IClaimLinkTemplate) => void;
  // Grid/list cards fill their container (default). Single capped previews (e.g. OneLink
  // summary) set this false to keep the aspect-ratio, shadowed card layout.
  fillWidth?: boolean;
}

export enum UploadControlModeEnum {
  Global = 'global',
  PageOverride = 'page-override',
}

export type TTemplateMediaFieldName =
  | typeof GLOBAL_APP_BAR_LOGO_IMAGE
  | typeof GLOBAL_HEADER_MEDIA
  | typeof GLOBAL_HERO_SECTION_IMAGE
  | typeof COVER_IMAGE;

export interface IBaseUploadControlProps {
  mode: UploadControlModeEnum;
  dataAction?: string;
}

export interface IColorValueProps {
  globalValue?: RGBColor;
  overrideValue?: RGBColor;
  defaultValue: RGBColor;
}

export interface IImageUrlValueProps {
  globalValue?: string;
  overrideValue?: string;
  defaultValue: string;
}

export interface IImageUrlWithFitValueProps {
  globalImageUrl?: string;
  overrideImageUrl?: string;
  globalImageFit?: ImageFitEnum;
  overrideImageFit?: ImageFitEnum;
}

export enum ClaimLinkTemplateActionTypeEnum {
  Save = 'save',
  Publish = 'publish',
}

export interface IClaimLinkTemplateErrorResponse {
  uid?: string;
  message: string;
  errors: IClaimLinkTemplateErrors;
}

export interface IClaimLinkTemplateErrors {
  department_ids?: string;
  name?: string;
  status?: string;
  header?: {
    app_bar?: {
      background_color?: string;
      logo_image_size?: string;
    };
    hero_section?: {
      background_color?: string;
    };
  };
  buttons?: {
    action?: {
      color?: string;
      radius?: string;
    };
    stepper?: {
      color?: string;
    };
  };
  pages?: {
    pick_your_gift?: {
      header_text?: string;
    };
    pick_your_option?: {
      header_text?: string;
    };
    shipping_page?: {
      header_text?: string;
    };
  };
}

export enum TemplateValidationModalEnum {
  None = 'none',
  Error = 'error',
}

export interface IValidationErrorClearingOptions {
  validationErrors: IClaimLinkTemplateErrors | null;
  validationModal?: TemplateValidationModalEnum;
  activePageId?: ClaimLinkEditorPageEnum;
}

export interface ITimeUnit {
  value: number;
  singular: string;
  plural: string;
}

export interface IPageErrors {
  pageName: string;
  errors: string[];
}

export interface IParsedTemplateErrors {
  pageErrors: IPageErrors[];
  totalIssues: number;
  nonPageErrors: string[];
}

export interface IValidatedFieldConfig {
  /** Field constant from templates.ts (e.g., 'headerText') */
  fieldConstant: string;
  /**
   * Path to error in validation errors object
   * Use {pageKey} placeholder for page-specific fields (e.g., 'pages.{pageKey}.header_text')
   * Omit placeholder for global fields (e.g., 'department_ids')
   */
  errorPath: string;
}

export enum IframeExperienceEventsEnum {
  Init = 'init',
  Navigate = 'navigate',
  SetExperience = 'set_experience',
  Ready = 'ready',
  Ping = 'ping',
}

export enum IframeStatusEnum {
  Loading = 'loading',
  Ready = 'ready',
  Error = 'error',
}
